// ─── Premier calendrier — onboarding (porté MoPCal) ──────────────
// Affiché par l'écran Calendrier quand l'espace n'a AUCUN calendrier : on
// propose d'en créer un (au lieu d'en créer un en silence). Repris de MoPCal
// (PremierCalendrierScreen), version suite : type + nom + icône + couleur.
import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, ScrollView, StyleSheet, ActivityIndicator } from 'react-native';
import { useUI, useT, useAuth, useEspace, PageHeader } from '@mop/core';
import { TYPES_CALENDRIER, creerCalendrierSuite } from './services/calendriersEspace';

const ICONES = ['📅', '🗓️', '👶', '🧒', '🏠', '💼', '🎯', '🌟'];
const COULEURS = ['#6C4FE6', '#E5397B', '#2E7D32', '#F57C00', '#0097A7', '#7B1FA2'];

export default function PremierCalendrier({ onCree }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { user } = useAuth();
  const { espaceActif } = useEspace();
  const espaceId = espaceActif?.id || null;

  const [type, setType] = useState('general');
  const [nom, setNom] = useState('');
  const [emoji, setEmoji] = useState('📅');
  const [couleur, setCouleur] = useState('#6C4FE6');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const choisirType = (t) => { setType(t); setEmoji(t === 'coparentalite' ? '👶' : '📅'); };

  async function creer() {
    if (!nom.trim()) { setErr(t('premierCal.errNom')); return; }
    if (!espaceId) { setErr(t('premierCal.errEspace')); return; }
    setBusy(true); setErr('');
    try {
      const id = await creerCalendrierSuite(espaceId, { nom: nom.trim(), emoji, type, couleur }, { uid: user?.uid, nom: user?.prenom || user?.email || '' });
      onCree?.(id);
    } catch (e) { setErr(e?.message || t('premierCal.errCreation')); setBusy(false); }
  }

  return (
    <View style={s.page}>
      <PageHeader titre={t('calendrier.titre')} iconeTitre="📅" />
      <ScrollView contentContainerStyle={s.corps} keyboardShouldPersistTaps="handled">
        <Text style={s.bienvenue}>🎉 {t('premierCal.bienvenue')}</Text>
        <Text style={s.sous}>{t('premierCal.sousTitre', { nom: espaceActif?.nom })}</Text>

        <View style={s.card}>
          <Text style={s.lbl}>{t('historique.dType')}</Text>
          <View style={s.typeRow}>
            {TYPES_CALENDRIER.map(it => (
              <TouchableOpacity key={it.id} style={[s.typeBtn, type === it.id && s.typeBtnOn]} onPress={() => choisirType(it.id)}>
                <Text style={s.typeIcn}>{it.emoji}</Text>
                <Text style={[s.typeTxt, type === it.id && s.typeTxtOn]}>{t('calendrier.typeCal_' + it.id)}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={s.lbl}>{t('listes.nom')}</Text>
          <TextInput style={s.input} value={nom} onChangeText={(v) => { setNom(v); setErr(''); }}
            placeholder={type === 'coparentalite' ? t('profil.prenomEnfantPlaceholder') : t('premierCal.nomPlaceholder')} placeholderTextColor={UI.faint} autoFocus maxLength={40} />

          <Text style={s.lbl}>{t('profil.icone')}</Text>
          <View style={s.wrap}>
            {ICONES.map(ic => (
              <TouchableOpacity key={ic} style={[s.iconeBtn, emoji === ic && { borderColor: couleur, backgroundColor: couleur + '22' }]} onPress={() => setEmoji(ic)}>
                <Text style={s.iconeEmoji}>{ic}</Text>
              </TouchableOpacity>
            ))}
          </View>

          <Text style={s.lbl}>{t('profil.couleur')}</Text>
          <View style={s.wrap}>
            {COULEURS.map(c => (
              <TouchableOpacity key={c} style={[s.couleurBtn, { backgroundColor: c }, couleur === c && s.couleurBtnOn]} onPress={() => setCouleur(c)} />
            ))}
          </View>

          {!!err && <Text style={s.err}>{err}</Text>}

          <TouchableOpacity style={[s.btn, { backgroundColor: couleur }, busy && { opacity: 0.6 }]} disabled={busy} onPress={creer}>
            {busy ? <ActivityIndicator color="#fff" /> : <Text style={s.btnTxt}>{t('premierCal.creer')}</Text>}
          </TouchableOpacity>
        </View>

        <Text style={s.note}>ℹ️ {t('premierCal.note')}</Text>
      </ScrollView>
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  page: { flex: 1, backgroundColor: UI.bg },
  corps: { padding: 18, alignItems: 'center' },
  bienvenue: { fontSize: 18, fontWeight: '800', color: UI.ink, marginTop: 8, marginBottom: 6 },
  sous: { fontSize: 13.5, color: UI.muted, textAlign: 'center', lineHeight: 20, marginBottom: 20, maxWidth: 340 },
  card: { width: '100%', maxWidth: 420, backgroundColor: UI.card, borderRadius: 18, borderWidth: 1, borderColor: UI.border, padding: 18 },
  lbl: { fontSize: 12, fontWeight: '800', color: UI.faint, textTransform: 'uppercase', letterSpacing: 0.4, marginTop: 12, marginBottom: 8 },
  typeRow: { flexDirection: 'row', gap: 10 },
  typeBtn: { flex: 1, borderWidth: 1.5, borderColor: UI.border, borderRadius: 12, padding: 12, alignItems: 'center', backgroundColor: UI.champ },
  typeBtnOn: { borderColor: UI.accent, backgroundColor: UI.accentTint },
  typeIcn: { fontSize: 20, marginBottom: 4 },
  typeTxt: { fontSize: 13.5, fontWeight: '700', color: UI.muted },
  typeTxtOn: { color: UI.accent },
  input: { backgroundColor: UI.champ, borderRadius: 12, paddingHorizontal: 14, paddingVertical: 12, fontSize: 16, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  wrap: { flexDirection: 'row', flexWrap: 'wrap', gap: 9 },
  iconeBtn: { width: 44, height: 44, borderRadius: 12, borderWidth: 2, borderColor: UI.border, backgroundColor: UI.champ, alignItems: 'center', justifyContent: 'center' },
  iconeEmoji: { fontSize: 18 },
  couleurBtn: { width: 36, height: 36, borderRadius: 18, borderWidth: 3, borderColor: 'transparent' },
  couleurBtnOn: { borderColor: UI.ink },
  err: { fontSize: 12.5, color: '#C62828', marginTop: 12 },
  btn: { borderRadius: 13, paddingVertical: 15, alignItems: 'center', marginTop: 16 },
  btnTxt: { fontSize: 16, fontWeight: '800', color: '#fff' },
  note: { fontSize: 12, color: UI.faint, textAlign: 'center', lineHeight: 18, maxWidth: 360, marginTop: 16 },
});
